import { fetchWithAuth } from './apiUtils';
import { getAuthToken } from './authService';
import { API_URL } from '../constants/api';

const API_BASE_URL = `${API_URL}/subscriptions`;

/**
 * Servicio para gestión de suscripciones
 */
const subscriptionService = {
  /**
   * Obtener todas las suscripciones con paginación
   * @param {Object} options - Opciones de consulta
   * @param {number} options.page - Número de página (default: 1)
   * @param {number} options.limit - Elementos por página (default: 10)
   * @param {string} options.search - Término de búsqueda
   * @param {string} options.estado - Filtrar por estado
   * @param {boolean} options.expiring_soon - Filtrar próximas a vencer
   * @returns {Promise} - Promesa con la lista de suscripciones paginada
   */
  async getAllSubscriptions(options = {}) {
    const token = getAuthToken();
    
    // Construir parámetros de consulta
    const queryParams = new URLSearchParams();
    if (options.page) queryParams.append('page', options.page);
    if (options.limit) queryParams.append('limit', options.limit);
    if (options.search) queryParams.append('search', options.search);
    if (options.estado) queryParams.append('estado', options.estado);
    if (options.expiring_soon !== undefined) queryParams.append('expiring_soon', options.expiring_soon);
    
    const url = queryParams.toString() ? `${API_BASE_URL}?${queryParams}` : API_BASE_URL;
    return await fetchWithAuth(url, {}, token);
  },

  /**
   * Obtener estadísticas de suscripciones
   * @returns {Promise} - Promesa con las estadísticas
   */
  async getSubscriptionStats() {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/stats`, {}, token);
  },

  /**
   * Crear una nueva suscripción
   * @param {Object} subscriptionData - Datos de la suscripción
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async createSubscription(subscriptionData) {
    const token = getAuthToken();
    return await fetchWithAuth(API_BASE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(subscriptionData),
    }, token);
  },

  /**
   * Actualizar una suscripción
   * @param {string} id - ID de la suscripción
   * @param {Object} subscriptionData - Datos actualizados de la suscripción
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async updateSubscription(id, subscriptionData) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(subscriptionData),
    }, token);
  },

  /**
   * Eliminar una suscripción
   * @param {string} id - ID de la suscripción
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async deleteSubscription(id) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${id}`, {
      method: 'DELETE',
    }, token);
  },

  /**
   * Obtener contactos disponibles para suscribir
   * @returns {Promise} - Promesa con la lista de contactos disponibles
   */
  async getAvailableContacts() {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/available-contacts`, {}, token);
  },

  /**
   * Obtener contactos que pueden ser pagadores
   * @returns {Promise} - Promesa con la lista de pagadores potenciales
   */
  async getPotentialPayers() {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/potential-payers`, {}, token);
  },

  /**
   * Obtener una suscripción por ID
   * @param {string} subscriptionId - ID de la suscripción
   * @returns {Promise} - Promesa con la suscripción
   */
  async getSubscriptionById(subscriptionId) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${subscriptionId}`, {}, token);
  },
};

export default subscriptionService; 